import React, { useState, useEffect, useRef } from 'react';
import { FolderPlus, Loader2, X } from 'lucide-react';

/**
 * Shared "Create / Rename Folder" modal for the reports and dashboards
 * modules. Replaces window.prompt() which we used to use for folder
 * creation — that prompt() can't enforce validation, can't show a loading
 * spinner, can't render the design system, and looks like a Chrome
 * dialog. This modal handles all of that.
 *
 * Props:
 *   open               — show/hide.
 *   mode               — 'create' | 'rename'. Drives copy + button label.
 *   initialName        — pre-filled value (used for rename).
 *   existingNames      — array of names already taken; surfaces a
 *                        validation error before submit.
 *   subtitle           — small text under the title, e.g. "Organize your
 *                        reports". Defaults to a generic line.
 *   onClose            — called for cancel or backdrop click.
 *   onSave(name)       — async or sync. While the returned promise is
 *                        pending the modal shows a spinner and disables
 *                        the buttons. If it throws, the error message is
 *                        rendered inside the modal.
 *
 * Keyboard:
 *   Enter on the input  → submit (if valid).
 *   Escape              → close.
 */
const CreateFolderModal = ({
  open,
  mode = 'create',
  initialName = '',
  existingNames = [],
  subtitle,
  onClose,
  onSave,
}) => {
  const [name, setName] = useState(initialName);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  const isRename = mode === 'rename';

  // Reset every time the modal reopens so prior state doesn't leak across
  // sessions. We also auto-focus the input which the browser's `autoFocus`
  // attribute doesn't reliably do for portaled dialogs.
  useEffect(() => {
    if (open) {
      setName(initialName);
      setSubmitting(false);
      setError(null);
      // setTimeout pushes the focus past the render commit so the input
      // exists in the DOM by the time we focus it.
      const t = setTimeout(() => inputRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [open, initialName]);

  // Escape-to-close. Skipping when submitting so an in-flight create
  // doesn't get orphaned.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !submitting) onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, submitting, onClose]);

  if (!open) return null;

  const trimmed = name.trim();
  const isDuplicate = trimmed
    && trimmed !== initialName
    && existingNames.some((n) => n.toLowerCase() === trimmed.toLowerCase());
  const canSubmit = trimmed.length > 0 && !isDuplicate && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    try {
      setSubmitting(true);
      setError(null);
      await onSave(trimmed);
      // The owner is responsible for closing on success — we don't call
      // onClose() here because the parent typically also runs a toast and
      // resets sidebar state in the same handler.
    } catch (e) {
      setSubmitting(false);
      setError(e?.message || 'Couldn\'t save the folder. Please try again.');
    }
  };

  const handleBackdrop = (e) => {
    if (submitting) return;
    if (e.target === e.currentTarget) onClose?.();
  };

  return (
    <div
      onClick={handleBackdrop}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
    >
      <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-sm p-6 relative">
        <button
          type="button"
          onClick={() => !submitting && onClose?.()}
          className="absolute right-3 top-3 p-1 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors disabled:opacity-40"
          disabled={submitting}
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
            <FolderPlus className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <h2 className="text-base font-bold text-slate-800">
              {isRename ? 'Rename Folder' : 'Create Folder'}
            </h2>
            <p className="text-xs text-slate-500">
              {subtitle || (isRename ? 'Update the folder name' : 'Organize and group related items')}
            </p>
          </div>
        </div>

        <label className="block text-xs font-medium text-slate-600 mb-1.5">
          Folder Name
        </label>
        <input
          ref={inputRef}
          value={name}
          onChange={(e) => { setName(e.target.value); setError(null); }}
          onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
          placeholder="e.g. Sales, Marketing, Q4 Reviews"
          disabled={submitting}
          maxLength={64}
          className={`w-full px-3 py-2.5 text-sm border rounded-xl focus:outline-none focus:ring-2 bg-slate-50 focus:bg-white mb-1 transition-colors ${
            isDuplicate || error
              ? 'border-rose-300 focus:ring-rose-400'
              : 'border-slate-200 focus:ring-indigo-500'
          }`}
        />
        <div className="min-h-[18px] mb-3">
          {isDuplicate && (
            <p className="text-[11px] text-rose-600">A folder named "{trimmed}" already exists.</p>
          )}
          {error && !isDuplicate && (
            <p className="text-[11px] text-rose-600">{error}</p>
          )}
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => !submitting && onClose?.()}
            disabled={submitting}
            className="flex-1 py-2.5 text-sm font-medium border border-slate-200 text-slate-600 rounded-xl hover:bg-slate-50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!canSubmit}
            className="flex-1 py-2.5 text-sm font-bold bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-1.5"
          >
            {submitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {isRename ? 'Save' : 'Create Folder'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateFolderModal;
